import { useEffect, useState } from "react";
import { DonationsResponse } from "@/services/donations.service";
import { routes } from "@/services/donations.service";
import useSWR, { preload } from "swr";
import { fetcherV2 } from "@gogive/services";

interface Props {
  initialPage: number;
  limit: number;
  sortBy: string;
  sortDirection: "ASC" | "DESC";
  select?: string[];
  campaignId?: number;
  fundraisingCampaignId?: string;
  donorId?: number;
  type?: string;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export function useDonations({
  initialPage,
  limit,
  sortBy,
  sortDirection,
  select,
  campaignId,
  fundraisingCampaignId,
  donorId,
  type,
  search,
  status,
  startDate,
  endDate,
}: Props) {
  const [page, setPage] = useState<number>(initialPage);
  const [pages, setPages] = useState<number>(0);

  useEffect(() => {
    setPage(initialPage);
  }, [
    initialPage,
    limit,
    sortBy,
    sortDirection,
    type,
    search,
    campaignId,
    fundraisingCampaignId,
    donorId,
    status,
    startDate,
    endDate,
  ]);

  const { data, error, mutate } = useSWR(
    () => [
      routes.donations,
      {
        page,
        limit,
        sortBy,
        sortDirection,
        select,
        campaignId,
        fundraisingCampaignId,
        donorId,
        type,
        search,
        status,
        startDate,
        endDate,
      },
    ],
    fetcherV2<DonationsResponse>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
      keepPreviousData: true,
    }
  );

  const preloadData = (increment: -1 | 1) => {
    preload(
      [
        routes.donations,
        {
          page: page + increment,
          limit,
          sortBy,
          sortDirection,
          select,
          campaignId,
          fundraisingCampaignId,
          donorId,
          type,
          search,
          status,
          startDate,
          endDate,
        },
      ],
      fetcherV2
    );
  };

  useEffect(() => {
    if (!data) return;
    const fromApi =
      typeof data.pages === "number" && !Number.isNaN(data.pages)
        ? data.pages
        : Math.ceil((data.total ?? 0) / limit);
    setPages(fromApi);
  }, [data, limit]);

  return {
    donations: data?.result ?? [],
    total: data?.total ?? 0,
    page: page + 1,
    pageIndex: page,
    pages,
    next: () => setPage((p) => p + 1),
    previous: () => setPage((p) => p - 1),
    setPageIndex: setPage,
    canPrevious: page > 0,
    canNext: page + 1 < pages,
    isLoading: !error && !data,
    isError: error,
    pageSize: limit,
    refresh: mutate,
    preloadData,
  };
}
